// Frank mirrors the picked ManufacturingLineList into the address bar so the selection survives a page refresh
// and the link can be shared. Each section owns one query parameter, manufacturing_line_<index> -- the SAME
// name and compact machine-id format the legacy Manufacturing Cost Calculator uses, so a link reads the same
// across both.
//
// The value is the ManufacturingLineList#identifier: its manufacturing lines' identifiers joined by "|"
// (ManufacturingLine#identifier is "manufacturing_line:offset:<printId>:<dieId>" for an offset pair,
// "manufacturing_line:flexo:<id>" for a combined flexo line, or "manufacturing_line:die_cutting:<id>" for a
// die-cutting-only one). The address bar carries only the machine ids -- everything after
// "manufacturing_line:<type>:" -- so it reads "manufacturing_line_0=10031-12008" instead of the percent-encoded
// identifier. The id COUNT recovers the type of the two PRINTING shapes on the way back (2 ids -> offset pair,
// 1 id -> flexo); a die-cutting-only manufacturing line carries one id too, so its token is MARKED to keep the
// two apart (see DIE_CUTTING_ONLY_PREFIX). Either way the round trip reproduces the identifier exactly -- the
// same string the listing exposed and the solutions endpoint keys on.
//
// The legacy page reads the same parameter and has no die-cutting-only manufacturing line, so a marked token
// fails its numeric parse there and is dropped as "no selection" -- the right answer, not a loss.
//
// A dynamically nested part set's manufacturing line carries its parts in the identifier ("<identifier>@<part ids>",
// Frank::ManufacturingLine#plan_identifier_piece); its token carries them after a "~", their ids joined by ".",
// so "manufacturing_line_2=16502-16512~216.217" -- which the legacy page drops the same way.

const parameterName = (index) => `manufacturing_line_${index}`;

const MACHINE_ID_SEPARATOR = '-';
const LINE_SEPARATOR = '_';
const LINE_IDENTIFIER_SEPARATOR = '|';

// The machine ids inside a line identifier: everything after "manufacturing_line:<type>:".
const machineIdsOf = (lineIdentifier) => lineIdentifier.split(':').slice(2);

const lineTypeOf = (lineIdentifier) => lineIdentifier.split(':')[1];

// A DIE-CUTTING-ONLY line carries ONE machine id, exactly like a combined flexo one, so the id COUNT can no
// longer recover its type -- a part_type that is never printed is made on a cutter alone (hard cardboard,
// issue #5694). Its token is marked, and only its token: every URL a carton or flexo section has ever written
// still reads and writes byte-identically.
const DIE_CUTTING_ONLY_PREFIX = 'd';
const DIE_CUTTING_ONLY_TYPE = 'die_cutting';

const PARTS_MARK = '@';
const PARTS_TOKEN_SEPARATOR = '~';
const PART_ID_SEPARATOR_IN_IDENTIFIER = '-';
const PART_ID_SEPARATOR_IN_TOKEN = '.';

// Serialize one line identifier to its machine ids joined by "-" ("<printId>-<dieId>", "<id>" for flexo, or
// "d<id>" for a die-cutting-only one).
const serializeMachineLine = (lineIdentifier) => {
  const ids = machineIdsOf(lineIdentifier).join(MACHINE_ID_SEPARATOR);

  return lineTypeOf(lineIdentifier) === DIE_CUTTING_ONLY_TYPE ? `${DIE_CUTTING_ONLY_PREFIX}${ids}` : ids;
};

// One plan identifier piece: a manufacturing line's token, followed by its part set's when it has one.
const serializeLine = (piece) => {
  const [lineIdentifier, parts] = piece.split(PARTS_MARK);
  if (parts === undefined) { return serializeMachineLine(lineIdentifier); }

  const partsToken = parts.split(PART_ID_SEPARATOR_IN_IDENTIFIER).join(PART_ID_SEPARATOR_IN_TOKEN);
  return `${serializeMachineLine(lineIdentifier)}${PARTS_TOKEN_SEPARATOR}${partsToken}`;
};

const parseLine = (token) => {
  const pieces = token.split(PARTS_TOKEN_SEPARATOR);
  if (pieces.length > 2) { return null; }
  const [machineToken, partsToken] = pieces;
  const lineIdentifier = parseMachineLine(machineToken);
  if (partsToken === undefined || lineIdentifier === null) { return lineIdentifier; }

  const partIds = partsToken.split(PART_ID_SEPARATOR_IN_TOKEN);
  if (!partIds.every((id) => id !== '' && !Number.isNaN(Number(id)))) { return null; }
  return `${lineIdentifier}${PARTS_MARK}${partIds.join(PART_ID_SEPARATOR_IN_IDENTIFIER)}`;
};

// Parse one token back to a line identifier, reconstructing the type from its mark and its machine id count
// ("d<id>" -> die-cutting only, 2 ids -> offset pair, 1 id -> flexo combined). Returns null for a malformed
// token (an empty or non-numeric id, or an id count other than 1 or 2) so a corrupt / stale-format param is
// rejected rather than restored as a bogus manufacturing line list.
const parseMachineLine = (token) => {
  if (token.startsWith(DIE_CUTTING_ONLY_PREFIX)) {
    const id = token.slice(DIE_CUTTING_ONLY_PREFIX.length);
    if (id === '' || Number.isNaN(Number(id))) { return null; }

    return `manufacturing_line:${DIE_CUTTING_ONLY_TYPE}:${id}`;
  }

  const ids = token.split(MACHINE_ID_SEPARATOR);
  if (!ids.every((id) => id !== '' && !Number.isNaN(Number(id)))) { return null; }
  if (ids.length === 2) { return `manufacturing_line:offset:${ids[0]}:${ids[1]}`; }
  if (ids.length === 1) { return `manufacturing_line:flexo:${ids[0]}`; }
  return null;
};

// The URL value for a ManufacturingLineList identifier: each line's machine ids, lines joined by "_". This
// round-trips exactly to the "|"-joined identifier the listing exposed, so the stored value and the identifier
// it stands for can never drift. Null / empty identifier yields an empty string.
export const manufacturingLineListParameterValue = (identifier) => (identifier || '')
  .split(LINE_IDENTIFIER_SEPARATOR)
  .filter(Boolean)
  .map(serializeLine)
  .join(LINE_SEPARATOR);

// The picked ManufacturingLineList's identifier for this section (the "|"-joined manufacturing line
// identifiers), or null when none is selected or the stored value is malformed. If ANY manufacturing line fails
// to parse the whole param is treated as "no selection" (falls back to the listing) rather than restoring a
// partial / bogus one.
export const readSelectedManufacturingLineList = (index) => {
  const value = new URLSearchParams(window.location.search).get(parameterName(index));
  if (!value) { return null; }
  const lines = value.split(LINE_SEPARATOR).map(parseLine);
  if (lines.some((line) => line === null)) { return null; }
  return lines.length > 0 ? lines.join(LINE_IDENTIFIER_SEPARATOR) : null;
};

// Keep the address bar in sync with this section's picked ManufacturingLineList so a refresh / shared link
// reproduces it.
// replaceState (not pushState) is deliberate -- this is a mirror, not in-page navigation: it adds no history
// entries, so Back never lands mid-flow. A same-origin relative URL is used so only the query string changes.
export const writeSelectedManufacturingLineList = (index, identifier) => {
  const url = new URL(window.location.href);
  url.searchParams.set(parameterName(index), manufacturingLineListParameterValue(identifier));
  window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
};

// Remove this section's picked ManufacturingLineList from the address bar (used by "back to machines" / a
// cleared selection).
export const clearSelectedManufacturingLineList = (index) => {
  const url = new URL(window.location.href);
  url.searchParams.delete(parameterName(index));
  window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
};

// ---------------------------------------------------------------------------------------------------------
// THE SECTION'S FILTERS -- the bobbin, the machine types, the reductions and the area-ratio cap.
//
// They travel WITH the ManufacturingLineList above, because its identifier only means anything against the
// listing it came from. Held only in component state they were lost the moment the section remounted -- which
// the guided setup wizard does on every close (the page's own ManufacturingLineLists is not mounted while the
// wizard is open), and a refresh / shared link does too. The section then came back on the server's DEFAULTS:
// the filter block no longer showed what the user had chosen, the picked ManufacturingLineList was dropped as
// stale because the default listing does not hold it, and the whole section re-listed and re-scored, since its
// request URL no longer matched the one the wizard's answers are cached under (see
// ManufacturingLineResponseCache).
//
// One parameter per filter, each suffixed with the section index like manufacturing_line_<index> above:
//   printing_machine_types_0=digital     the machine types ticked, comma-joined
//   die_cutting_machine_types_0=         present-but-EMPTY means "nothing ticked" -- NOT the same as ABSENT,
//                                        which means "not spoken yet" and lets the server apply its own default
//   disabled_filters_0=smallest_die_cutting_machine_of_each_type   the reductions switched OFF
//   area_ratio_percent_0=25              the area-ratio cap
//   bobbin_0=true                        the bobbin tick
// A filter still at its default is left OUT rather than written, so an ordinary visitor's link stays as short as
// it was and "absent" keeps its meaning. The bobbin is written as soon as it is TOUCHED, including a false that
// happens to match the part_type's own default: what its default is depends on the part_type (carton starts
// unticked, carton_lamination ticked), so only "untouched" can be left unsaid.

const VALUE_SEPARATOR = ',';
const MACHINE_TYPES_KEY_SUFFIX = '_machine_types';
const DISABLED_FILTERS_NAME = 'disabled_filters';
const AREA_RATIO_PERCENT_NAME = 'area_ratio_percent';
const BOBBIN_NAME = 'bobbin';
const MATERIAL_USER_PRICE_ID_NAME = 'material';
const CARTON_GSM_NAME = 'carton_gsm';

const sectionParameterName = (name, index) => `${name}_${index}`;

// Set the parameter, or remove it when the value is null/undefined ("at its default, say nothing").
const setOrDelete = (url, name, value) => {
  if (value === null || value === undefined) {
    url.searchParams.delete(name);
    return;
  }
  url.searchParams.set(name, value);
};

const replaceQuery = (url) => window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);

// A machine-type filter is recognised by its KEY, which the server names "<something>_machine_types"
// (printing_machine_types, die_cutting_machine_types). Keying off that convention rather than off a list here
// means a machine-type filter added server-side round-trips without having to be named here as well -- and it is
// applied on BOTH sides, so what this module writes is exactly what it can read back and clear again. A key that
// does not follow the convention is NOT persisted: writing one would leave a parameter that nothing restores and
// nothing ever removes, which is worse than not remembering it. Name a new filter to match and it works.
const isMachineTypesKey = (filterKey) => filterKey.endsWith(MACHINE_TYPES_KEY_SUFFIX);

// The machine-type parameter names this section holds. `_machine_types_1` cannot match `..._machine_types_11`:
// the whole suffix including the index must end the name.
const machineTypeParameterNames = (parameters, index) => [...parameters.keys()]
  .filter((name) => name.endsWith(`${MACHINE_TYPES_KEY_SUFFIX}_${index}`));

// The machine types this section's address bar holds, as { printing_machine_types: ['digital'] }. A filter row
// the user has not touched is simply absent, which is what the component's empty {} means too.
export const readSectionMachineTypes = (index) => {
  const parameters = new URLSearchParams(window.location.search);

  return machineTypeParameterNames(parameters, index).reduce((machineTypes, name) => {
    const value = parameters.get(name);
    const filterKey = name.slice(0, -`_${index}`.length);
    return { ...machineTypes, [filterKey]: value === '' ? [] : value.split(VALUE_SEPARATOR) };
  }, {});
};

// The reductions switched OFF for this section; [] when none is (the default).
export const readSectionDisabledFilters = (index) => {
  const value = new URLSearchParams(window.location.search).get(sectionParameterName(DISABLED_FILTERS_NAME, index));
  if (!value) { return []; }
  return value.split(VALUE_SEPARATOR).filter(Boolean);
};

// The area-ratio cap as a percent, or null when the address bar does not carry one (-> the page's own default).
// A corrupt parameter falls back to the default rather than listing against nonsense: non-numeric and negative
// are rejected like the input itself rejects them, and so is a value that is not FINITE -- "Infinity" and an
// overflowing "1e400" both survive Number.isNaN, and either would be handed to the listing, the solve and the
// scoring as the cap.
export const readSectionAreaRatioPercent = (index) => {
  const value = new URLSearchParams(window.location.search).get(sectionParameterName(AREA_RATIO_PERCENT_NAME, index));
  if (value === null || value.trim() === '') { return null; }
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) { return null; }
  return parsed;
};

// The value the section's solution was SELECTED with, stored with the section (Frank::SectionParameters, the
// bobbin_enabled / material_user_price_id / carton_gsm columns of window.user_quote_nav_sections), or undefined.
// The readers below fall back to it when the address bar carries none.
const storedSectionValue = (index, column) => {
  const navSections = window.user_quote_nav_sections;
  if (!Array.isArray(navSections)) { return undefined; }
  const navSection = navSections.find((section) => Number(section.sort_order) === Number(index));
  if (!navSection || navSection[column] === null || navSection[column] === undefined) { return undefined; }
  return navSection[column];
};

// The address bar's value for a section parameter, else the stored one as a String (null when neither).
const sectionParameterOrStored = (name, index, column) => {
  const value = new URLSearchParams(window.location.search).get(sectionParameterName(name, index));
  if (value !== null) { return value; }
  const stored = storedSectionValue(index, column);
  return stored === undefined ? null : String(stored);
};

// The owner's chosen material row (a UserUnitPrice id), or null when none has been picked (-> the server's
// lowest-id auto-pick). The Malzeme dropdown mirrors it here so a refresh / shared link comes back on the same
// material the lines were calculated with; without one, the material the section's solution was selected with.
export const readSectionMaterialUserPriceId = (index) => {
  const value = sectionParameterOrStored(MATERIAL_USER_PRICE_ID_NAME, index, 'material_user_price_id');
  if (value === null || value.trim() === '') { return null; }
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

// The carton GSM the owner picked for a carton_lamination section (the Malzeme-row dropdown), or null when none
// has been picked (-> the server's default, 300). Mirrored so a refresh / shared link comes back on the same GSM
// the printing machines were found for; without one, the GSM the section's solution was selected with.
export const readSectionCartonGsm = (index) => {
  const value = sectionParameterOrStored(CARTON_GSM_NAME, index, 'carton_gsm');
  if (value === null || value.trim() === '') { return null; }
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

// The bobbin tick, or undefined when it has not been touched (-> the part_type's own default). Undefined and
// false are DIFFERENT answers here, which is why this returns undefined rather than false for an absent one. Without
// one in the address bar, the tick the section's solution was selected with.
export const readSectionBobbin = (index) => {
  const value = sectionParameterOrStored(BOBBIN_NAME, index, 'bobbin_enabled');
  if (value === null) { return undefined; }
  return value === 'true';
};

// Mirror the section's filters into the address bar. Every machine-type parameter of this section is dropped
// first, so a row that goes back to "not spoken" leaves the URL instead of lingering. Pass null/undefined for a
// filter that is at its default -- the caller owns what its default is (the area-ratio cap comes from a window
// setting), so this module never has to guess it.
export const writeSectionFilters = (index,
  { machineTypes, disabledFilters, areaRatioPercent, materialUserPriceId, cartonGsm }) => {
  const url = new URL(window.location.href);

  machineTypeParameterNames(url.searchParams, index).forEach((name) => url.searchParams.delete(name));
  Object.entries(machineTypes || {})
    .filter(([filterKey]) => isMachineTypesKey(filterKey))
    .forEach(([filterKey, types]) => {
      url.searchParams.set(sectionParameterName(filterKey, index), types.join(VALUE_SEPARATOR));
    });

  setOrDelete(url, sectionParameterName(DISABLED_FILTERS_NAME, index),
    (disabledFilters || []).length > 0 ? disabledFilters.join(VALUE_SEPARATOR) : null);
  setOrDelete(url, sectionParameterName(AREA_RATIO_PERCENT_NAME, index),
    areaRatioPercent === null || areaRatioPercent === undefined ? null : String(areaRatioPercent));
  setOrDelete(url, sectionParameterName(MATERIAL_USER_PRICE_ID_NAME, index),
    materialUserPriceId === null || materialUserPriceId === undefined ? null : String(materialUserPriceId));
  setOrDelete(url, sectionParameterName(CARTON_GSM_NAME, index),
    cartonGsm === null || cartonGsm === undefined ? null : String(cartonGsm));

  replaceQuery(url);
};

// Mirror the bobbin tick. It is written on its own because it belongs to the section ABOVE this listing (the
// material sheets are found over it, and the listing over those sheets) -- LayoutAndQuoteBuilderNavSection owns
// it, ManufacturingLineLists only draws its row.
export const writeSectionBobbin = (index, enabled) => {
  const url = new URL(window.location.href);
  setOrDelete(url, sectionParameterName(BOBBIN_NAME, index),
    enabled === null || enabled === undefined ? null : String(enabled));
  replaceQuery(url);
};
